import { useEffect } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import livelinxAuthLogo from '@/assets/images/livelinx-auth-logo.png';
import { useAuthTokenContext } from '@/auth/KeycloakProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ImageWithLoader } from '@/components/ui/image-with-loader';
import { PolicyModal } from '@/modules/policy-modal';

import authConfig from '../config/auth-config.json';
import { useAuth } from '../hooks/useAuth';
import { AuthFormProps } from '../types';

/**
 * Authentication Form Component
 *
 * SIGNUP DISABLED BY DESIGN - Only login is available.
 * Contact admin for new account creation.
 *
 * Features:
 * - Keycloak OIDC authentication
 * - Auto-redirect after successful login
 * - Branding customization via config
 */
export const AuthForm = ({ config = authConfig }: AuthFormProps) => {
  const navigate = useNavigate();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  const { login, loading } = useAuth(config);
  const { tokenStatus } = useAuthTokenContext();

  const handleCheckboxClick = () => {
    if (!termsAccepted) {
      setShowPolicyModal(true);
    } else {
      setTermsAccepted(false);
    }
  };

  const handlePolicyAccept = () => {
    setTermsAccepted(true);
    setShowPolicyModal(false);
  };

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  // Redirect when authenticated
  useEffect(() => {
    if (tokenStatus === 'valid') {
      navigate(config.navigation.redirectAfterAuth);
    }
  }, [tokenStatus, navigate, config.navigation.redirectAfterAuth]);

  const isLoading = loading || tokenStatus === 'loading';

  return (
    <div className="flex min-h-screen items-center justify-center bg-livelinx-grey100 p-4">
      <Card className="w-full max-w-md bg-white p-8 shadow-white">
        <div className="mb-8 text-center">
          {config.branding.showLogo && (
            <div className="mb-6 flex justify-center">
              <ImageWithLoader
                src={livelinxAuthLogo}
                alt={config.branding.logoAlt}
                className="h-auto w-32 object-contain"
                containerClassName="flex justify-center w-32"
                skeletonClassName="rounded-md h-20"
                loading="eager"
                fetchPriority="high"
                width={128}
                height={103}
                onClick={undefined}
                linkTo="https://www.livelinx.com"
              />
            </div>
          )}
          <h1 className="mb-2 font-heading text-4xl text-livelinx-purple200">
            {config.form.title}
          </h1>
          <p className="text-livelinx-grey200">{config.form.loginSubtitle}</p>
        </div>

        <div className="space-y-4">
          {/* Terms and conditions checkbox */}
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={handleCheckboxClick}
              className="mt-1"
            />
            <label
              htmlFor="terms"
              className="cursor-pointer select-none text-sm leading-relaxed text-livelinx-text"
              onClick={(e) => {
                e.preventDefault();
                handleCheckboxClick();
              }}
            >
              {config.form.termsCheckbox.label}
            </label>
          </div>

          <Button
            type="button"
            onClick={() => void handleLogin()}
            disabled={isLoading || !termsAccepted}
            className="w-full bg-primary hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? config.form.buttons.loading : config.form.buttons.login}
          </Button>
        </div>
      </Card>

      {/* Policy Modal */}
      <PolicyModal isOpen={showPolicyModal} onClose={() => {}} onAccept={handlePolicyAccept} />
    </div>
  );
};
